import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaPlus, FaEdit } from 'react-icons/fa';
import RoleService, { permissionsToArray, arrayToPermissions } from '../../services/RoleService';

const RoleModal = ({ isOpen, onClose, role = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
            permissions: [
          { name: 'User Management', granted: false },
          { name: 'Role Management', granted: false },
          { name: 'Manage Opportunities', granted: false },
          { name: 'Read Only', granted: false }
        ]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (role) {
      // Edit mode
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: permissionsToArray(role.permissions)
      });
    } else {
      // Create mode
      setFormData({
        name: '',
        description: '',
        permissions: [
          { name: 'User Management', granted: false },
          { name: 'Role Management', granted: false },
          { name: 'Manage Opportunities', granted: false },
          { name: 'Read Only', granted: false }
        ]
      });
    }
  }, [role]);

  const handlePermissionToggle = (index) => {
    const newPermissions = [...formData.permissions];
    newPermissions[index].granted = !newPermissions[index].granted;
    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const permissions = arrayToPermissions(formData.permissions);
      
      if (role) {
        // Update existing role
        await RoleService.updateRole(role.id, {
          name: formData.name,
          description: formData.description || undefined,
          permissions
        });
      } else {
        // Create new role
        await RoleService.createRole({
          name: formData.name,
          description: formData.description || undefined,
          permissions
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {role ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions
              </label>
              <div className="space-y-2">
                {formData.permissions.map((permission, index) => (
                  <div key={index} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`permission-${index}`}
                      checked={permission.granted}
                      onChange={() => handlePermissionToggle(index)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor={`permission-${index}`}
                      className="ml-2 text-sm text-gray-700"
                    >
                      {permission.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  'Saving...'
                ) : (
                  <>
                    {role ? <FaEdit className="w-4 h-4 mr-1" /> : <FaPlus className="w-4 h-4 mr-1" />}
                    {role ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleModal; 